import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import '../../styles/portal-ui-polish.css';

interface MenuItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  className?: string;
  divider?: boolean;
}

interface DropdownMenuProps {
  items: MenuItem[];
  buttonClassName?: string;
  menuClassName?: string;
}

export default function DropdownMenu({ items, buttonClassName = '', menuClassName = '' }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        menuRef.current && !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 192; // 48 * 4 (w-48 in tailwind)
      const menuHeight = items.filter(item => !item.divider).length * 40 + items.filter(item => item.divider).length * 9 + 16; // Approximate height
      
      let top = buttonRect.bottom + 8; // Default to below button
      let left = buttonRect.right - menuWidth; // Align to right edge of button
      
      // Check if menu would go off screen
      if (top + menuHeight > window.innerHeight - 20) {
        // Position above button if not enough space below
        top = buttonRect.top - menuHeight - 8;
      }
      
      // Ensure menu doesn't go off left edge
      if (left < 10) {
        left = 10;
      }
      
      // Ensure menu doesn't go off right edge
      if (left + menuWidth > window.innerWidth - 10) {
        left = window.innerWidth - menuWidth - 10;
      }

      setMenuPosition({ top, left });
    }
  }, [isOpen, items]);

  const handleItemClick = (item: MenuItem) => {
    item.onClick();
    setIsOpen(false);
  };

  return (
    <>
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`inline-flex items-center justify-center p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-150 ${buttonClassName}`}
        >
          <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {isOpen && createPortal(
        <div 
          ref={menuRef}
          style={{ 
            position: 'fixed', 
            top: `${menuPosition.top}px`, 
            left: `${menuPosition.left}px`,
            zIndex: 9999 
          }}
          className={`portal-dropdown w-48 ${menuClassName}`}
        >
          <div className="py-1 max-h-60 overflow-y-auto">
            {items.map((item, index) => (
              <React.Fragment key={index}>
                {item.divider ? (
                  <div className="border-t border-gray-100 my-1" />
                ) : (
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className={`group flex w-full items-center px-4 py-2 text-sm transition-colors duration-150 hover:bg-gray-50 ${
                      item.className || 'text-gray-700 hover:text-gray-900'
                    }`}
                  >
                    {item.icon && (
                      <span className="mr-3 text-gray-400 group-hover:text-gray-500">
                        {item.icon}
                      </span>
                    )}
                    {item.label}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}